import type { EntityType, FlatContentLayersType } from "@config";
import type { ITableCopiedCell, ITableSettings } from "@library-elements/table";
import {
  evaluateCalculation,
  formatCalculationResult,
} from "@library-elements/table/utils/evaluate-calculation";
import { getWholeEntityLayers } from "./getWholeEntityLayers";

export interface ICellChildrenPayload {
  rootIds: string[];
  entities: Record<string, EntityType>;
}

// v2 table-cells clipboard payload. `version` absent = legacy cells-only
// (still accepted on read); `cellChildren` maps SOURCE cellId → that cell's
// element subtrees in stack order; `calcText` maps SOURCE cellId → the
// formula's rendered value at copy time (formulas don't survive a paste into
// a populated cell — see the design spec).
export interface ITableClipboardPayload {
  feature: "table-cells-shortcut";
  version?: 2;
  entityEntryRootId: string;
  cells: ITableCopiedCell[];
  cellChildren?: Record<string, ICellChildrenPayload>;
  calcText?: Record<string, string>;
}

export const serializeCellRange = (
  tableId: string,
  cellsIds: string[],
  flatContentLayers: FlatContentLayersType,
): ITableClipboardPayload | null => {
  const entity = flatContentLayers[tableId];
  if (entity?.settings.name !== "Table") return null;
  const { tableData, tableCols, tableRows } =
    entity.settings as unknown as ITableSettings;

  const cells: ITableCopiedCell[] = [];
  const calcText: Record<string, string> = {};
  const cellsIdSet = new Set(cellsIds);
  for (let i = 0; i < tableData.length; i++) {
    const cell = tableData[i];
    if (!cellsIdSet.has(cell.id)) continue;
    const col = i % tableCols;
    const row = Math.floor(i / tableCols);
    cells.push({ col, row, data: cell });
    if (cell.calculation?.formula) {
      const result = evaluateCalculation(cell.calculation.formula, {
        tableData,
        tableCols,
        tableRows,
        cellCoord: { col, row },
      });
      calcText[cell.id] =
        result.kind === "error" ? "" : formatCalculationResult(result);
    }
  }
  if (!cells.length) return null;

  const payload: ITableClipboardPayload = {
    feature: "table-cells-shortcut",
    entityEntryRootId: tableId,
    cells,
    ...(Object.keys(calcText).length ? { calcText } : {}),
  };

  const cellChildren: Record<string, ICellChildrenPayload> = {};
  for (const childId of entity.childIds) {
    const child = flatContentLayers[childId];
    const cellId = child?.settings.cellId as string | undefined;
    if (!cellId || !cellsIdSet.has(cellId)) continue;
    const bucket = (cellChildren[cellId] ??= { rootIds: [], entities: {} });
    bucket.rootIds.push(childId);
    // By-reference from the Redux draft — safe only because the caller
    // JSON.stringifies immediately (same contract as copyEntityToClipboard).
    const wholeLayers = getWholeEntityLayers(childId, flatContentLayers);
    if (wholeLayers) Object.assign(bucket.entities, wholeLayers.entities);
  }
  if (Object.keys(cellChildren).length) {
    payload.version = 2;
    payload.cellChildren = cellChildren;
  }
  return payload;
};
